import { createPortal } from 'react-dom'
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import cn from 'classnames'
import { AlertTriangle, Loader2, Play } from 'lucide-react'
import {
    claimMediaPlayback,
    clearMediaTrackOrigin,
    createMediaPlaybackOwnerId,
    getMediaPlaybackState,
    isMediaKeyPlayingInDock,
    isMediaKeyPresentedInDock,
    isMediaElementAtEnd,
    isMediaPlaybackLoaded,
    markMediaPlaybackLoaded,
    persistMediaElementPlaybackState,
    playMediaPlaybackOwner,
    registerMediaTrack,
    releaseMediaPlayback,
    safeMediaCurrentTime,
    seekMediaKey,
    setMediaKeyVolume,
    setMediaPlaybackState,
    setMediaTrackOrigin,
    subscribeToMediaDock,
    subscribeToMediaPlaybackState,
    isElementUserVisible,
    isMediaOriginHostedInline,
    getMediaDockHostedSurfaceElement,
    findMountedOwnerForMediaKey,
    syncMediaPlaybackOwner,
    transferMediaPlaybackControlFromDock,
    unregisterMediaPlaybackOwner,
} from 'state/mediaPlaybackCoordinator'
import { hostedNativeMediaEnabled } from 'lib/hostedMediaPlayback'
import { applyMediaElementCaptions, readMediaElementCaptionsState } from 'lib/mediaCaptions'
import useHostedMediaRuntime from 'components/shared/MediaDock/useHostedMediaRuntime'
import { toggleTrackInPlaylist } from 'components/shared/MediaDock/mediaDockEvents'
import { useMediaKeyInPlaylist } from 'components/shared/MediaDock/useMediaKeyInPlaylist'
import useCachedAnswer from 'refactor/hooks/api/useCachedAnswer'
import dockStyles from 'components/shared/MediaDock/media-dock.module.scss'
import MediaPlayButton from 'components/shared/MediaPlayButton'
import VideoChrome from 'components/shared/VideoChrome'
import { isVideoFullscreenActive, toggleVideoFullscreen } from './videoFullscreen'
import styles from './video-player-core.module.scss'

const appendRetryParam = (url, retryCount) => {
    if (!url || retryCount <= 0) return url
    const separator = url.includes('?') ? '&' : '?'
    return `${url}${separator}break_cache=true&retry=${retryCount}`
}

const deriveTitleFromSrc = src => {
    if (!src) return null
    try {
        const pathname = new URL(src, window.location.origin).pathname
        const fileName = decodeURIComponent(pathname.split('/').pop() || '')
        // Only use real-looking file names; bare storage keys (no extension)
        // read as gibberish in the dock.
        return fileName.includes('.') ? fileName : null
    } catch {
        return null
    }
}

const clampMediaVolume = value => {
    const numericValue = Number(value)
    return Number.isFinite(numericValue) ? Math.max(0, Math.min(1, numericValue)) : null
}

const VIDEO_PLAYBACK_RATES = [0.5, 0.75, 1, 1.25, 1.5, 2]
const HOVER_VIDEO_CONTROLS_QUERY = '(hover: hover) and (pointer: fine)'

const canUseHoverVideoControls = () =>
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia(HOVER_VIDEO_CONTROLS_QUERY).matches

// Tap/click can park focus on a control (notably Android taps); only
// keyboard-visible focus should pin the chrome open.
const matchesFocusVisible = element => {
    try {
        return element.matches(':focus-visible')
    } catch {
        return true
    }
}

const clampVideoPlaybackRate = value => {
    const numericValue = Number(value)
    return Number.isFinite(numericValue) && numericValue > 0 ? numericValue : null
}

const mediaElementEffectiveVolume = mediaEl => {
    if (!mediaEl) return null
    return clampMediaVolume(mediaEl.volume)
}

const VideoPlayerCore = ({
    mediaKey,
    src,
    type = undefined,
    trackTitle = undefined,
    trackAnswerId = undefined,
    trackQuestId = undefined,
    trackArtifactOrdinalId = undefined,
    trackArtifactOwnerId = undefined,
    posterSrc = null,
    width = null,
    height = null,
    videoRef = null,
    className = undefined,
    rootStyle = undefined,
    containerClassName = undefined,
    // Inline overrides for the media container. An explicit height here pins the box (CSS then
    // ignores aspect-ratio), which callers use to reserve constant space under static geometry.
    containerStyle = undefined,
    videoClassName = undefined,
    posterClassName = undefined,
    preloadVideoLink = false,
    preloadVideoAfterPoster = false,
    preloadVideoOnHover = false,
    keepChromeVisibleOnHover = false,
    preload = 'metadata',
    autoPlayOnStart = true,
    loadWithoutPoster = true,
    showFirstFramePreview = false,
    restoreLoadedStateOnMount = true,
    retryPoster = false,
    retryLimit = 3,
    fallbackAspectRatio = '1 / 1',
    useNativeAspectRatio = false,
    renderOverlay = null,
    onLoadedMetadata = null,
    onPlay = null,
    onPause = null,
    onEnded = null,
    onResolveVideoRetrySrc = null,
    onVideoError = null,
    onPosterLoad = null,
    onPosterError = null,
    topRightControls = null,
}) => {
    const internalVideoRef = useRef(null)
    const mediaElementRef = videoRef || internalVideoRef
    const rootRef = useRef(null)
    const previewContainerRef = useRef(null)
    const playbackContainerRef = useRef(null)
    const ownerIdRef = useRef(createMediaPlaybackOwnerId('video-player'))
    const restoredOnMountRef = useRef(false)
    const previousMediaKeyRef = useRef(mediaKey)
    const playHerePendingRef = useRef(false)
    const pendingProgrammaticSeekTimeRef = useRef(null)
    const pendingProgrammaticVolumeRef = useRef(null)
    const chromeAutoHideTimeoutRef = useRef(null)
    const chromeIntentActiveRef = useRef(false)

    const hasPoster = Boolean(posterSrc)
    const hasFirstFramePreview = Boolean(showFirstFramePreview && !hasPoster && src)
    const hasPreview = hasPoster || hasFirstFramePreview
    const [currentSrc, setCurrentSrc] = useState(src || null)
    const [videoError, setVideoError] = useState(false)
    const [videoRetryCount, setVideoRetryCount] = useState(0)
    const [posterRetryCount, setPosterRetryCount] = useState(0)
    const [settledPosterUrl, setSettledPosterUrl] = useState(null)
    const [isVideoPreloadArmed, setIsVideoPreloadArmed] = useState(false)
    const [isDockOwned, setIsDockOwned] = useState(false)
    const [isLoaded, setIsLoaded] = useState(() => {
        return Boolean(
            (!hasPreview && loadWithoutPoster) ||
                (restoreLoadedStateOnMount && isMediaPlaybackLoaded(mediaKey)),
        )
    })
    const [shouldAutoPlay, setShouldAutoPlay] = useState(false)
    // Hosted playback: the media dock owns the single <video> for this track
    // and parks it over this frame, so navigation never rebuilds it. The
    // decision is constant for the session.
    const hostedVideo = hostedNativeMediaEnabled() && Boolean(mediaKey)
    const hostedRuntime = useHostedMediaRuntime({
        mediaKey,
        enabled: hostedVideo,
        originRef: playbackContainerRef,
        placement: 'visual',
    })
    const isHostedHere = hostedRuntime.isHostedHere
    const hostedPlayPendingRef = useRef(false)
    const hostedNativeCallbacksRef = useRef(null)
    const reportedHostedMetadataRef = useRef(null)
    const reportedHostedErrorRef = useRef(null)

    // Hosting parks the dock over the video box specifically, not the whole
    // card, so that box has to be a registered origin. It only renders once
    // playback starts, hence a callback ref rather than an effect.
    const setPlaybackContainerNode = useCallback(
        node => {
            const previous = playbackContainerRef.current
            if (previous === node) return
            if (previous && hostedVideo && mediaKey) clearMediaTrackOrigin(mediaKey, previous)
            playbackContainerRef.current = node
            if (node && hostedVideo && mediaKey) setMediaTrackOrigin(mediaKey, node)
        },
        [hostedVideo, mediaKey],
    )
    const [forcedAspectRatio, setForcedAspectRatio] = useState(null)
    const [chromeState, setChromeState] = useState({
        isPlaying: false,
        currentTime: 0,
        duration: 0,
        volume: 1,
        playbackRate: 1,
        captionsAvailable: false,
        captionsEnabled: false,
    })
    const [isChromeDraggingSeek, setIsChromeDraggingSeek] = useState(false)
    const [usesHoverChrome, setUsesHoverChrome] = useState(canUseHoverVideoControls)
    const [isChromeSurfaceActive, setIsChromeSurfaceActive] = useState(false)
    const [isFullscreen, setIsFullscreen] = useState(false)

    // Paused (or ended) keeps the chrome up; while playing it only shows on
    // recent pointer/tap intent so the bar stops covering the video.
    const showChrome = !chromeState.isPlaying || isChromeSurfaceActive || isChromeDraggingSeek
    const showVideoElement = shouldAutoPlay || isLoaded || (!hasPreview && loadWithoutPoster)
    const posterUrl = useMemo(
        () => appendRetryParam(posterSrc, posterRetryCount),
        [posterRetryCount, posterSrc],
    )
    const containerAspectRatio = useMemo(() => {
        if (forcedAspectRatio) return forcedAspectRatio
        if (width && height) return `${width} / ${height}`
        return fallbackAspectRatio || null
    }, [fallbackAspectRatio, forcedAspectRatio, height, width])
    const isCurrentPosterSettled = !posterUrl || settledPosterUrl === posterUrl
    const shouldPreloadVideo = Boolean(
        preloadVideoLink &&
            currentSrc &&
            (!preloadVideoAfterPoster || showVideoElement || !posterUrl || isCurrentPosterSettled),
    )
    const shouldPreloadVideoOnIntent = Boolean(
        preloadVideoLink &&
            currentSrc &&
            preloadVideoOnHover &&
            (showVideoElement || isVideoPreloadArmed),
    )
    const shouldRenderVideoPreload = preloadVideoOnHover
        ? shouldPreloadVideoOnIntent
        : shouldPreloadVideo

    useEffect(() => {
        const mediaKeyChanged = previousMediaKeyRef.current !== mediaKey
        previousMediaKeyRef.current = mediaKey

        setVideoError(false)
        setVideoRetryCount(0)
        setPosterRetryCount(0)
        setShouldAutoPlay(false)
        setForcedAspectRatio(null)
        restoredOnMountRef.current = false
        playHerePendingRef.current = false
        setSettledPosterUrl(null)
        setIsVideoPreloadArmed(false)
        setIsChromeSurfaceActive(false)
        setIsFullscreen(false)

        const shouldRestoreLoaded = Boolean(
            (!hasPreview && loadWithoutPoster) ||
                (restoreLoadedStateOnMount && isMediaPlaybackLoaded(mediaKey)),
        )
        setIsLoaded(shouldRestoreLoaded)
        setCurrentSrc(current => {
            if (mediaKeyChanged) return src || null
            if (current && shouldRestoreLoaded) return current
            return src || null
        })
        setChromeState(previous => ({
            ...previous,
            isPlaying: false,
            currentTime: 0,
            duration: 0,
            playbackRate: 1,
            captionsAvailable: false,
            captionsEnabled: false,
        }))
    }, [hasPreview, loadWithoutPoster, mediaKey, restoreLoadedStateOnMount, src])

    useEffect(() => {
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function')
            return undefined

        const mediaQuery = window.matchMedia(HOVER_VIDEO_CONTROLS_QUERY)
        const syncHoverChromeMode = () => {
            setUsesHoverChrome(mediaQuery.matches)
            setIsChromeSurfaceActive(false)
        }

        syncHoverChromeMode()
        mediaQuery.addEventListener?.('change', syncHoverChromeMode)
        return () => mediaQuery.removeEventListener?.('change', syncHoverChromeMode)
    }, [])

    const clearChromeAutoHideTimeout = useCallback(() => {
        if (!chromeAutoHideTimeoutRef.current || typeof window === 'undefined') return
        window.clearTimeout(chromeAutoHideTimeoutRef.current)
        chromeAutoHideTimeoutRef.current = null
    }, [])

    const getFullscreenTarget = useCallback(
        () =>
            (isHostedHere ? getMediaDockHostedSurfaceElement() : null) ||
            playbackContainerRef.current,
        [isHostedHere],
    )

    const getFullscreenVideoElement = useCallback(
        () =>
            hostedVideo
                ? findMountedOwnerForMediaKey(mediaKey)?.mediaEl || null
                : mediaElementRef.current,
        [hostedVideo, mediaElementRef, mediaKey],
    )

    const isChromeFullscreenActive = useCallback(() => {
        return isVideoFullscreenActive({
            fullscreenTarget: getFullscreenTarget(),
            videoElement: getFullscreenVideoElement(),
        })
    }, [getFullscreenTarget, getFullscreenVideoElement])

    const scheduleChromeAutoHide = useCallback(
        (delay = 1100) => {
            if (typeof window === 'undefined') return

            clearChromeAutoHideTimeout()
            chromeAutoHideTimeoutRef.current = window.setTimeout(() => {
                chromeAutoHideTimeoutRef.current = null
                if (chromeIntentActiveRef.current) return

                const activeElement =
                    typeof document !== 'undefined' ? document.activeElement : null
                const playbackContainer = playbackContainerRef.current
                if (
                    keepChromeVisibleOnHover &&
                    usesHoverChrome &&
                    !isChromeFullscreenActive() &&
                    playbackContainer?.matches(':hover')
                ) {
                    return
                }
                if (
                    typeof Node !== 'undefined' &&
                    activeElement instanceof Node &&
                    playbackContainer?.contains(activeElement) &&
                    matchesFocusVisible(activeElement)
                ) {
                    return
                }

                setIsChromeSurfaceActive(false)
            }, delay)
        },
        [
            clearChromeAutoHideTimeout,
            isChromeFullscreenActive,
            keepChromeVisibleOnHover,
            usesHoverChrome,
        ],
    )

    useEffect(() => clearChromeAutoHideTimeout, [clearChromeAutoHideTimeout])

    useEffect(() => {
        if (typeof document === 'undefined') return undefined

        const handleFullscreenChange = () => {
            clearChromeAutoHideTimeout()
            const fullscreenActive = isChromeFullscreenActive()
            setIsFullscreen(fullscreenActive)
            if (fullscreenActive) {
                setIsChromeSurfaceActive(true)
                scheduleChromeAutoHide(1400)
            }
        }

        document.addEventListener('fullscreenchange', handleFullscreenChange)
        document.addEventListener('webkitfullscreenchange', handleFullscreenChange)
        return () => {
            document.removeEventListener('fullscreenchange', handleFullscreenChange)
            document.removeEventListener('webkitfullscreenchange', handleFullscreenChange)
        }
    }, [clearChromeAutoHideTimeout, isChromeFullscreenActive, scheduleChromeAutoHide])

    const syncChromeFromElement = useCallback(
        (videoEl = mediaElementRef.current, options = {}) => {
            if (!videoEl) return

            const nextCurrentTime = safeMediaCurrentTime(videoEl, 0)
            const nextDuration =
                Number.isFinite(videoEl.duration) && videoEl.duration > 0 ? videoEl.duration : 0
            const nextVolume = mediaElementEffectiveVolume(videoEl)
            const nextPlaybackRate = clampVideoPlaybackRate(videoEl.playbackRate)
            const captions = readMediaElementCaptionsState(videoEl)

            setChromeState(previous => ({
                isPlaying: !videoEl.paused && !videoEl.ended,
                currentTime:
                    options.forceTime || !isChromeDraggingSeek
                        ? nextCurrentTime
                        : previous.currentTime,
                duration: nextDuration || previous.duration,
                volume: nextVolume ?? previous.volume,
                playbackRate: nextPlaybackRate ?? previous.playbackRate,
                captionsAvailable: captions.available,
                captionsEnabled: captions.enabled,
            }))
        },
        [isChromeDraggingSeek, mediaElementRef],
    )

    useEffect(() => {
        if (!mediaKey || !restoreLoadedStateOnMount) return undefined
        return subscribeToMediaPlaybackState(mediaKey, nextState => {
            if (nextState?.isLoaded) setIsLoaded(true)
        })
    }, [mediaKey, restoreLoadedStateOnMount])

    useEffect(() => {
        if (!mediaKey) {
            setIsDockOwned(false)
            return undefined
        }

        // A hosted runtime parked over this very frame is playing *here*, so it
        // must not offer to bring itself back.
        const updateDockOwnedState = () =>
            setIsDockOwned(
                isMediaKeyPresentedInDock(mediaKey) &&
                    !isMediaOriginHostedInline(mediaKey, playbackContainerRef.current),
            )
        updateDockOwnedState()
        return subscribeToMediaDock(updateDockOwnedState)
    }, [mediaKey])

    useEffect(() => {
        // Hosted: the dock owns the only element. Registering an owner here
        // would register one with no runtime behind it, and the coordinator
        // reads an element-less owner as "playing" — so its unmount during
        // navigation would look like playback being torn away and the dock
        // would adopt (and restart) the track.
        if (hostedVideo) return
        syncMediaPlaybackOwner(ownerIdRef.current, mediaKey, mediaElementRef.current)
    }, [hostedVideo, mediaKey, mediaElementRef, showVideoElement])

    // Register this video with the media dock so it shows up as a
    // playlist-history track and can keep playing after this player unmounts.
    // Use a layout effect so no-poster/unprocessed videos with native controls
    // are dockable before the user can start playback.
    useLayoutEffect(() => {
        if (!mediaKey || !currentSrc) return undefined

        registerMediaTrack(mediaKey, {
            kind: 'video',
            src: currentSrc,
            posterSrc: posterSrc || null,
            title: trackTitle || deriveTitleFromSrc(currentSrc) || 'Video',
            answerId: trackAnswerId || null,
            questId: trackQuestId || null,
            ...(trackArtifactOrdinalId
                ? {
                      artifactOrdinalId: trackArtifactOrdinalId,
                      artifactOwnerId: trackArtifactOwnerId || null,
                  }
                : {}),
        })

        const originEl = rootRef.current
        setMediaTrackOrigin(mediaKey, originEl)
        return () => clearMediaTrackOrigin(mediaKey, originEl)
    }, [
        mediaKey,
        currentSrc,
        posterSrc,
        trackTitle,
        trackAnswerId,
        trackQuestId,
        trackArtifactOrdinalId,
        trackArtifactOwnerId,
    ])

    useEffect(() => {
        return () => unregisterMediaPlaybackOwner(ownerIdRef.current)
    }, [])

    const persistPlaybackState = useCallback(
        (overrides = {}) => {
            if (!mediaKey) return null
            const videoEl = mediaElementRef.current
            const previousSnapshot = getMediaPlaybackState(mediaKey)
            const playbackRate = clampVideoPlaybackRate(videoEl?.playbackRate)

            return persistMediaElementPlaybackState(
                mediaKey,
                videoEl,
                {
                    ...(playbackRate != null && overrides.playbackRate == null
                        ? { playbackRate }
                        : {}),
                    ...overrides,
                    isLoaded: overrides.isLoaded ?? previousSnapshot?.isLoaded ?? isLoaded,
                },
                {
                    skipAtEnd: overrides.currentTime == null,
                },
            )
        },
        [isLoaded, mediaElementRef, mediaKey],
    )

    useEffect(() => {
        if (!mediaKey) return undefined

        return subscribeToMediaPlaybackState(mediaKey, nextState => {
            if (!nextState) return

            const videoEl = mediaElementRef.current
            if (!videoEl || !nextState.isLoaded || !videoEl.paused || videoEl.ended) return
            if (isMediaKeyPlayingInDock(mediaKey)) return
            if (!Number.isFinite(nextState.currentTime) || nextState.currentTime <= 0.05) return

            const duration = Number.isFinite(videoEl.duration) ? videoEl.duration : 0
            if (duration > 0 && nextState.currentTime >= duration - 0.25) return

            try {
                if (Math.abs((videoEl.currentTime || 0) - nextState.currentTime) > 0.05) {
                    pendingProgrammaticSeekTimeRef.current = nextState.currentTime
                    videoEl.currentTime = nextState.currentTime
                }
            } catch {
                pendingProgrammaticSeekTimeRef.current = null
            }
        })
    }, [mediaElementRef, mediaKey])

    useEffect(() => {
        if (!mediaKey || !showVideoElement) return undefined

        const syncVolume = nextState => {
            const nextVolume = clampMediaVolume(nextState?.volume)
            const videoEl = mediaElementRef.current
            if (nextVolume == null || !videoEl) return

            try {
                if (Math.abs((videoEl.volume || 0) - nextVolume) > 0.001) {
                    pendingProgrammaticVolumeRef.current = nextVolume
                    videoEl.volume = nextVolume
                }
                setChromeState(previous =>
                    Math.abs(previous.volume - nextVolume) <= 0.001
                        ? previous
                        : { ...previous, volume: nextVolume },
                )
            } catch {
                pendingProgrammaticVolumeRef.current = null
            }
        }

        syncVolume(getMediaPlaybackState(mediaKey))
        return subscribeToMediaPlaybackState(mediaKey, syncVolume)
    }, [mediaElementRef, mediaKey, showVideoElement])

    useEffect(() => {
        if (!mediaKey || !showVideoElement) return undefined

        // The track's remembered speed and caption choice apply to this
        // element, whichever runtime set them last (the dock's, in cinema).
        const syncCaptionChromeFromElement = ({ persist = false } = {}) => {
            const videoEl = mediaElementRef.current
            if (!videoEl) return

            const captions = readMediaElementCaptionsState(videoEl)
            setChromeState(previous =>
                previous.captionsAvailable === captions.available &&
                previous.captionsEnabled === captions.enabled
                    ? previous
                    : {
                          ...previous,
                          captionsAvailable: captions.available,
                          captionsEnabled: captions.enabled,
                      },
            )
            if (
                persist &&
                captions.available &&
                getMediaPlaybackState(mediaKey)?.captions !== captions.enabled
            ) {
                setMediaPlaybackState(mediaKey, { captions: captions.enabled })
            }
        }

        const syncPlaybackPreferences = nextState => {
            const videoEl = mediaElementRef.current
            if (!videoEl) return

            const nextPlaybackRate = clampVideoPlaybackRate(nextState?.playbackRate)
            if (nextPlaybackRate != null) {
                try {
                    if (Math.abs((videoEl.playbackRate || 1) - nextPlaybackRate) > 0.001) {
                        videoEl.playbackRate = nextPlaybackRate
                    }
                    setChromeState(previous =>
                        Math.abs(previous.playbackRate - nextPlaybackRate) <= 0.001
                            ? previous
                            : { ...previous, playbackRate: nextPlaybackRate },
                    )
                } catch {}
            }

            if (typeof nextState?.captions === 'boolean') {
                applyMediaElementCaptions(videoEl, nextState.captions)
            }
            syncCaptionChromeFromElement()
        }

        syncPlaybackPreferences(getMediaPlaybackState(mediaKey))
        const videoEl = mediaElementRef.current
        const textTracks = videoEl?.textTracks
        const restoreLateCaptionPreference = () =>
            syncPlaybackPreferences(getMediaPlaybackState(mediaKey))
        const syncCaptionAvailability = () => syncCaptionChromeFromElement()
        const handleNativeCaptionChange = () => syncCaptionChromeFromElement({ persist: true })
        videoEl?.addEventListener('loadedmetadata', restoreLateCaptionPreference)
        textTracks?.addEventListener?.('addtrack', restoreLateCaptionPreference)
        textTracks?.addEventListener?.('removetrack', syncCaptionAvailability)
        textTracks?.addEventListener?.('change', handleNativeCaptionChange)
        const unsubscribe = subscribeToMediaPlaybackState(mediaKey, syncPlaybackPreferences)

        return () => {
            unsubscribe?.()
            videoEl?.removeEventListener('loadedmetadata', restoreLateCaptionPreference)
            textTracks?.removeEventListener?.('addtrack', restoreLateCaptionPreference)
            textTracks?.removeEventListener?.('removetrack', syncCaptionAvailability)
            textTracks?.removeEventListener?.('change', handleNativeCaptionChange)
        }
    }, [mediaElementRef, mediaKey, showVideoElement])

    useEffect(() => {
        if (!mediaKey || !showVideoElement) return undefined
        const videoEl = mediaElementRef.current
        if (!videoEl) return undefined

        const syncPlaybackState = options => {
            if (options?.skipAtEnd && isMediaElementAtEnd(videoEl)) return
            persistPlaybackState()
        }
        const handleTimeUpdate = () => syncPlaybackState()
        const handleSeeked = () => syncPlaybackState()
        const handlePlaying = () => syncPlaybackState()
        const handlePause = () => syncPlaybackState({ skipAtEnd: true })
        const doc = typeof document !== 'undefined' ? document : null
        const handleVisibilityChange = () => {
            if (doc?.hidden) syncPlaybackState()
        }

        videoEl.addEventListener('timeupdate', handleTimeUpdate)
        videoEl.addEventListener('seeked', handleSeeked)
        videoEl.addEventListener('playing', handlePlaying)
        videoEl.addEventListener('pause', handlePause)
        doc?.addEventListener('visibilitychange', handleVisibilityChange)

        return () => {
            syncPlaybackState()
            videoEl.removeEventListener('timeupdate', handleTimeUpdate)
            videoEl.removeEventListener('seeked', handleSeeked)
            videoEl.removeEventListener('playing', handlePlaying)
            videoEl.removeEventListener('pause', handlePause)
            doc?.removeEventListener('visibilitychange', handleVisibilityChange)
        }
    }, [mediaElementRef, mediaKey, persistPlaybackState, showVideoElement])

    useLayoutEffect(() => {
        if (!mediaKey || !showVideoElement || restoredOnMountRef.current) return undefined
        const videoEl = mediaElementRef.current
        if (!videoEl) return undefined
        const stored = getMediaPlaybackState(mediaKey)
        if (!Number.isFinite(stored?.currentTime) || stored.currentTime <= 0.05) return undefined

        const applyPlaybackState = () => {
            const duration = Number.isFinite(videoEl.duration) ? videoEl.duration : 0
            if (duration > 0 && stored.currentTime >= duration - 0.25) {
                restoredOnMountRef.current = true
                return
            }

            try {
                if (Math.abs((videoEl.currentTime || 0) - stored.currentTime) > 0.05) {
                    pendingProgrammaticSeekTimeRef.current = stored.currentTime
                    videoEl.currentTime = stored.currentTime
                }
                restoredOnMountRef.current = true
            } catch {
                pendingProgrammaticSeekTimeRef.current = null
            }
        }

        if (videoEl.readyState >= 1) {
            applyPlaybackState()
            return undefined
        }

        const handleLoadedMetadata = () => {
            applyPlaybackState()
            videoEl.removeEventListener('loadedmetadata', handleLoadedMetadata)
        }
        videoEl.addEventListener('loadedmetadata', handleLoadedMetadata)
        return () => videoEl.removeEventListener('loadedmetadata', handleLoadedMetadata)
    }, [mediaElementRef, mediaKey, showVideoElement])

    const playInlineFromCurrentDockTime = useCallback(() => {
        if (!mediaKey) return false
        syncMediaPlaybackOwner(ownerIdRef.current, mediaKey, mediaElementRef.current)
        return playMediaPlaybackOwner(ownerIdRef.current, { transferFromDock: true })
    }, [mediaElementRef, mediaKey])

    useLayoutEffect(() => {
        if (!playHerePendingRef.current || !showVideoElement) return

        playHerePendingRef.current = false
        playInlineFromCurrentDockTime()
    }, [playInlineFromCurrentDockTime, showVideoElement])

    // While hosted, the chrome reads the dock's runtime instead of a local
    // element (there isn't one).
    useEffect(() => {
        if (!hostedVideo) return
        const { snapshot } = hostedRuntime
        setChromeState(previous => {
            const next = {
                ...previous,
                isPlaying: snapshot.isPlaying,
                currentTime: isChromeDraggingSeek ? previous.currentTime : snapshot.currentTime,
                duration: snapshot.duration > 0 ? snapshot.duration : previous.duration,
                ...(snapshot.volume == null ? {} : { volume: snapshot.volume }),
                ...(snapshot.playbackRate == null ? {} : { playbackRate: snapshot.playbackRate }),
                captionsAvailable: snapshot.captionsAvailable === true,
                captionsEnabled: snapshot.captionsEnabled === true,
            }
            const changed = Object.keys(next).some(key => previous[key] !== next[key])
            return changed ? next : previous
        })
    }, [hostedRuntime, hostedVideo, isChromeDraggingSeek])

    useLayoutEffect(() => {
        if (!hostedVideo || !hostedPlayPendingRef.current || !playbackContainerRef.current) return
        hostedPlayPendingRef.current = false
        hostedRuntime.play({ source: 'play_here' })
    }, [hostedRuntime, hostedVideo, showVideoElement])

    const requestInlinePlayback = useCallback(() => {
        if (hostedVideo) {
            markMediaPlaybackLoaded(mediaKey)
            setIsLoaded(true)
            setShouldAutoPlay(false)
            // The video box renders with this state change, so a request now
            // would have no frame to park on; wait for it below.
            if (playbackContainerRef.current) hostedRuntime.play({ source: 'play_here' })
            else hostedPlayPendingRef.current = true
            return
        }

        markMediaPlaybackLoaded(mediaKey)
        setIsLoaded(true)
        setShouldAutoPlay(false)

        if (showVideoElement && playInlineFromCurrentDockTime()) {
            return
        }

        playHerePendingRef.current = true
    }, [hostedRuntime, hostedVideo, mediaKey, playInlineFromCurrentDockTime, showVideoElement])

    // The dock is leaving cinema mode with this video, which it took from this
    // post only to show it there — or its hosted runtime just left another
    // post showing this video as the user navigated here. While this post is
    // the one on screen, bring playback back here exactly as "Play here" does
    // — the same runtime is re-parked (hosted) or resumed inline
    // (element-backed) from its position.
    useEffect(() => {
        if (!mediaKey) return undefined
        return subscribeToMediaDock(event => {
            if (event?.type !== 'cinemaExit' && event?.type !== 'hostedReturn') return
            if (event.mediaKey !== mediaKey) return
            const rootEl = rootRef.current
            if (!rootEl || (event.originEl !== rootEl && !rootEl.contains(event.originEl))) return
            if (!isElementUserVisible(rootEl)) return
            requestInlinePlayback()
        })
    }, [mediaKey, requestInlinePlayback])

    const handleStartPlayback = useCallback(
        event => {
            event.preventDefault()
            event.stopPropagation()
            clearChromeAutoHideTimeout()
            setIsChromeSurfaceActive(true)
            scheduleChromeAutoHide(2600)
            setIsVideoPreloadArmed(true)
            const rect = previewContainerRef.current?.getBoundingClientRect()
            if (rect?.width > 0 && rect?.height > 0) {
                setForcedAspectRatio(rect.width / rect.height)
            }
            markMediaPlaybackLoaded(mediaKey)
            setIsLoaded(true)
            if (autoPlayOnStart) {
                requestInlinePlayback()
            }
        },
        [
            autoPlayOnStart,
            clearChromeAutoHideTimeout,
            mediaKey,
            requestInlinePlayback,
            scheduleChromeAutoHide,
        ],
    )
    const handleStartPlaybackKeyDown = useCallback(
        event => {
            if (event.key !== 'Enter' && event.key !== ' ') return
            handleStartPlayback(event)
        },
        [handleStartPlayback],
    )

    const handleLoadedMetadata = useCallback(
        event => {
            const videoWidth = Number(event.currentTarget.videoWidth)
            const videoHeight = Number(event.currentTarget.videoHeight)
            setForcedAspectRatio(
                useNativeAspectRatio && videoWidth > 0 && videoHeight > 0
                    ? `${videoWidth} / ${videoHeight}`
                    : null,
            )
            setIsLoaded(true)
            persistPlaybackState({ isLoaded: true })
            syncChromeFromElement(event.currentTarget, { forceTime: true })
            onLoadedMetadata?.(event)
        },
        [onLoadedMetadata, persistPlaybackState, syncChromeFromElement, useNativeAspectRatio],
    )

    const handleFirstFrameLoadedMetadata = useCallback(
        event => {
            const videoEl = event.currentTarget
            const videoWidth = Number(videoEl.videoWidth)
            const videoHeight = Number(videoEl.videoHeight)
            if (useNativeAspectRatio && videoWidth > 0 && videoHeight > 0) {
                setForcedAspectRatio(`${videoWidth} / ${videoHeight}`)
            }

            // Browsers do not consistently paint frame zero for an unplayed
            // video. Seek a hair into the first frame while keeping it paused.
            try {
                if (
                    Number.isFinite(videoEl.duration) &&
                    videoEl.duration > 0 &&
                    videoEl.currentTime < 0.001
                ) {
                    videoEl.currentTime = Math.min(0.01, videoEl.duration)
                }
            } catch {}
        },
        [useNativeAspectRatio],
    )

    const handlePlay = useCallback(
        event => {
            markMediaPlaybackLoaded(mediaKey)
            setIsLoaded(true)
            setShouldAutoPlay(false)
            syncMediaPlaybackOwner(ownerIdRef.current, mediaKey, mediaElementRef.current)
            claimMediaPlayback(ownerIdRef.current, { transferFromDock: true })
            persistPlaybackState({ isLoaded: true })
            syncChromeFromElement(event.currentTarget, { forceTime: true })
            setIsChromeSurfaceActive(true)
            scheduleChromeAutoHide(2000)
            onPlay?.(event)
        },
        [
            mediaElementRef,
            mediaKey,
            onPlay,
            persistPlaybackState,
            scheduleChromeAutoHide,
            syncChromeFromElement,
        ],
    )

    const handlePause = useCallback(
        event => {
            releaseMediaPlayback(ownerIdRef.current)
            if (!isMediaElementAtEnd(event.currentTarget)) {
                persistPlaybackState({
                    currentTime: safeMediaCurrentTime(event.currentTarget),
                    isLoaded: true,
                })
            }
            syncChromeFromElement(event.currentTarget, { forceTime: true })
            onPause?.(event)
        },
        [onPause, persistPlaybackState, syncChromeFromElement],
    )

    const handleEnded = useCallback(
        event => {
            releaseMediaPlayback(ownerIdRef.current, { ended: true, mediaKey })
            persistPlaybackState({ currentTime: 0, isLoaded: true })
            syncChromeFromElement(event.currentTarget, { forceTime: true })
            onEnded?.(event)
        },
        [mediaKey, onEnded, persistPlaybackState, syncChromeFromElement],
    )

    const handleVolumeChange = useCallback(
        event => {
            if (!mediaKey) return

            const nextVolume = mediaElementEffectiveVolume(event.currentTarget)
            if (nextVolume == null) return

            const pendingProgrammaticVolume = pendingProgrammaticVolumeRef.current
            const transferFromDock =
                pendingProgrammaticVolume == null ||
                Math.abs(nextVolume - pendingProgrammaticVolume) > 0.001
            pendingProgrammaticVolumeRef.current = null
            setChromeState(previous => ({ ...previous, volume: nextVolume }))
            setMediaKeyVolume(mediaKey, nextVolume, {
                ownerId: ownerIdRef.current,
                transferFromDock,
            })
        },
        [mediaKey],
    )

    const handlePlaybackRateChange = useCallback(
        event => {
            const nextPlaybackRate = clampVideoPlaybackRate(event.currentTarget?.playbackRate)
            if (nextPlaybackRate == null) return

            setChromeState(previous => ({ ...previous, playbackRate: nextPlaybackRate }))
            if (mediaKey) setMediaPlaybackState(mediaKey, { playbackRate: nextPlaybackRate })
        },
        [mediaKey],
    )

    const handleSeeking = useCallback(() => {
        if (!mediaKey) return
        const videoEl = mediaElementRef.current
        const pendingProgrammaticSeekTime = pendingProgrammaticSeekTimeRef.current
        if (
            videoEl &&
            pendingProgrammaticSeekTime != null &&
            Math.abs((videoEl.currentTime || 0) - pendingProgrammaticSeekTime) <= 0.25
        ) {
            pendingProgrammaticSeekTimeRef.current = null
            return
        }
        pendingProgrammaticSeekTimeRef.current = null
        transferMediaPlaybackControlFromDock(ownerIdRef.current)
    }, [mediaElementRef, mediaKey])

    const handleVideoTimeUpdate = useCallback(
        event => {
            // Playback state is persisted by the media-element listener above.
            // Avoid repainting a hidden control overlay on every video tick,
            // which competes with momentum scrolling in iOS WebViews.
            if (!showChrome) return

            const videoEl = event.currentTarget
            const nextCurrentTime = safeMediaCurrentTime(videoEl, 0)
            const nextDuration =
                Number.isFinite(videoEl.duration) && videoEl.duration > 0 ? videoEl.duration : 0

            setChromeState(previous => ({
                ...previous,
                currentTime: isChromeDraggingSeek ? previous.currentTime : nextCurrentTime,
                duration: nextDuration || previous.duration,
            }))
        },
        [isChromeDraggingSeek, showChrome],
    )

    const handleVideoSeeked = useCallback(
        event => {
            syncChromeFromElement(event.currentTarget, { forceTime: true })
        },
        [syncChromeFromElement],
    )

    const handleChromeTogglePlayback = useCallback(
        event => {
            event?.preventDefault?.()
            event?.stopPropagation?.()

            if (hostedVideo) {
                setIsChromeSurfaceActive(true)
                if (hostedRuntime.snapshot.isPlaying) hostedRuntime.pause()
                else hostedRuntime.play({ startAtBeginning: hostedRuntime.snapshot.isEnded })
                return
            }

            if (!showVideoElement) {
                requestInlinePlayback()
                return
            }

            const videoEl = mediaElementRef.current
            if (!videoEl) return

            // Keep the chrome up through the play/pause flip; syncing
            // isPlaying below would otherwise blank it for a frame before the
            // play event re-reveals it.
            setIsChromeSurfaceActive(true)

            if (!videoEl.paused && !videoEl.ended) {
                videoEl.pause()
                syncChromeFromElement(videoEl, { forceTime: true })
                return
            }

            if (!playInlineFromCurrentDockTime()) {
                videoEl.play?.()?.catch?.(() => {})
            }
            syncChromeFromElement(videoEl, { forceTime: true })
        },
        [
            hostedRuntime,
            hostedVideo,
            mediaElementRef,
            playInlineFromCurrentDockTime,
            requestInlinePlayback,
            showVideoElement,
            syncChromeFromElement,
        ],
    )

    const handleChromeSeek = useCallback(
        nextTime => {
            if (!Number.isFinite(nextTime) || nextTime < 0) return

            setChromeState(previous => ({ ...previous, currentTime: nextTime }))

            if (hostedVideo) {
                hostedRuntime.seekTo(nextTime)
                return
            }

            if (mediaKey) {
                seekMediaKey(mediaKey, nextTime, {
                    ownerId: ownerIdRef.current,
                    transferFromDock: true,
                })
                return
            }

            try {
                if (mediaElementRef.current) mediaElementRef.current.currentTime = nextTime
            } catch {}
        },
        [hostedRuntime, hostedVideo, mediaElementRef, mediaKey],
    )

    const handleChromeVolume = useCallback(
        nextVolume => {
            const volume = clampMediaVolume(nextVolume)
            if (volume == null) return

            setChromeState(previous => ({ ...previous, volume }))

            if (hostedVideo) {
                hostedRuntime.setVolume(volume)
                return
            }

            if (mediaKey) {
                setMediaKeyVolume(mediaKey, volume, {
                    ownerId: ownerIdRef.current,
                    transferFromDock: true,
                })
                return
            }

            try {
                if (mediaElementRef.current) mediaElementRef.current.volume = volume
            } catch {}
        },
        [hostedRuntime, hostedVideo, mediaElementRef, mediaKey],
    )

    const handleChromePlaybackRate = useCallback(
        nextPlaybackRate => {
            const playbackRate = clampVideoPlaybackRate(nextPlaybackRate)
            if (playbackRate == null) return

            setChromeState(previous => ({ ...previous, playbackRate }))

            if (hostedVideo) {
                hostedRuntime.setPlaybackRate(playbackRate)
                return
            }

            try {
                if (mediaElementRef.current) mediaElementRef.current.playbackRate = playbackRate
            } catch {}

            if (mediaKey) setMediaPlaybackState(mediaKey, { playbackRate })
        },
        [hostedRuntime, hostedVideo, mediaElementRef, mediaKey],
    )

    const handleChromeToggleCaptions = useCallback(() => {
        const enabled = !chromeState.captionsEnabled

        if (hostedVideo) {
            hostedRuntime.setCaptionsEnabled(enabled)
            return
        }

        const videoEl = mediaElementRef.current
        if (!videoEl) return
        applyMediaElementCaptions(videoEl, enabled)
        if (mediaKey) setMediaPlaybackState(mediaKey, { captions: enabled })
        const captions = readMediaElementCaptionsState(videoEl)
        setChromeState(previous => ({
            ...previous,
            captionsAvailable: captions.available,
            captionsEnabled: captions.enabled,
        }))
    }, [chromeState.captionsEnabled, hostedRuntime, hostedVideo, mediaElementRef, mediaKey])

    const inPlaylist = useMediaKeyInPlaylist(mediaKey)
    // Listeners other than the poster with this post in their playlist (answer
    // JSON playlist_count). The dock writes the server's count into the cached
    // answer after this viewer's own add or remove, so subscribe to it.
    const cachedAnswer = useCachedAnswer(trackAnswerId ? String(trackAnswerId) : null)
    const shownPlaylistCount = Number(cachedAnswer?.playlist_count) || 0
    const handleChromeTogglePlaylist = useCallback(() => {
        if (mediaKey) toggleTrackInPlaylist(mediaKey, 'media_player')
    }, [mediaKey])

    const handleChromeSeekStart = useCallback(event => {
        event?.stopPropagation?.()
        setIsChromeDraggingSeek(true)
    }, [])

    const handleChromeSeekEnd = useCallback(
        event => {
            event?.stopPropagation?.()
            setIsChromeDraggingSeek(false)
            syncChromeFromElement(mediaElementRef.current, { forceTime: true })
            setIsChromeSurfaceActive(true)
            scheduleChromeAutoHide()
        },
        [mediaElementRef, scheduleChromeAutoHide, syncChromeFromElement],
    )

    const handleChromeFullscreen = useCallback(
        event => {
            event?.preventDefault?.()
            event?.stopPropagation?.()
            event?.currentTarget?.blur?.()
            const fullscreenTarget =
                getFullscreenTarget() || previewContainerRef.current || rootRef.current
            toggleVideoFullscreen({
                fullscreenTarget,
                videoElement: getFullscreenVideoElement(),
            })
        },
        [getFullscreenTarget, getFullscreenVideoElement],
    )

    const handleVideoError = useCallback(
        async event => {
            const videoElement = event.currentTarget
            if (!hostedVideo) {
                releaseMediaPlayback(ownerIdRef.current)
                persistPlaybackState()
            }
            const nextRetryCount = videoRetryCount + 1

            if (nextRetryCount > retryLimit) {
                setVideoError(true)
                onVideoError?.(event)
                return
            }

            let retrySrc = null
            if (onResolveVideoRetrySrc) {
                retrySrc = await onResolveVideoRetrySrc({
                    retryCount: nextRetryCount,
                    currentSrc,
                    event,
                })
            }

            setVideoRetryCount(nextRetryCount)
            if (retrySrc && retrySrc !== currentSrc) {
                setCurrentSrc(retrySrc)
                return
            }

            window.setTimeout(() => {
                try {
                    videoElement?.load()
                } catch {}
            }, 1000)
        },
        [
            currentSrc,
            hostedVideo,
            onResolveVideoRetrySrc,
            onVideoError,
            persistPlaybackState,
            retryLimit,
            videoRetryCount,
        ],
    )

    // The post still owns sizing and source retry policy, even though the
    // element now belongs to the dock. Subscribe only while this is the
    // hosting surface so duplicate/offscreen posts cannot retry the runtime.
    hostedNativeCallbacksRef.current = { handleLoadedMetadata, handleVideoError }
    const hostedMediaElement = hostedVideo
        ? findMountedOwnerForMediaKey(mediaKey)?.mediaEl || null
        : null
    useEffect(() => {
        if (!isHostedHere || !hostedMediaElement) return undefined
        const metadata = event => {
            reportedHostedMetadataRef.current = { element: hostedMediaElement, src: currentSrc }
            hostedNativeCallbacksRef.current.handleLoadedMetadata(event)
        }
        const error = event => {
            reportedHostedErrorRef.current = hostedMediaElement.error
            hostedNativeCallbacksRef.current.handleVideoError(event)
        }
        hostedMediaElement.addEventListener('loadedmetadata', metadata)
        hostedMediaElement.addEventListener('error', error)

        const reported = reportedHostedMetadataRef.current
        if (
            hostedMediaElement.readyState >= 1 &&
            (reported?.element !== hostedMediaElement || reported?.src !== currentSrc)
        ) {
            metadata({ currentTarget: hostedMediaElement })
        }
        if (
            hostedMediaElement.error &&
            reportedHostedErrorRef.current !== hostedMediaElement.error
        ) {
            error({ currentTarget: hostedMediaElement })
        }
        return () => {
            hostedMediaElement.removeEventListener('loadedmetadata', metadata)
            hostedMediaElement.removeEventListener('error', error)
        }
    }, [currentSrc, hostedMediaElement, isHostedHere])

    const handlePosterError = useCallback(
        event => {
            setSettledPosterUrl(posterUrl)
            if (retryPoster) {
                setPosterRetryCount(count => Math.min(count + 1, retryLimit))
            }
            onPosterError?.(event)
        },
        [onPosterError, posterUrl, retryLimit, retryPoster],
    )

    const handlePosterLoad = useCallback(
        event => {
            const posterWidth = Number(event.currentTarget.naturalWidth)
            const posterHeight = Number(event.currentTarget.naturalHeight)
            if (useNativeAspectRatio && posterWidth > 0 && posterHeight > 0) {
                setForcedAspectRatio(`${posterWidth} / ${posterHeight}`)
            }
            setSettledPosterUrl(posterUrl)
            onPosterLoad?.(event)
        },
        [onPosterLoad, posterUrl, useNativeAspectRatio],
    )

    // The dock's video covers this frame while hosted, so the chrome has to
    // live above it — in the dock's own chrome layer.
    const renderChromeInDock = chrome => {
        if (!isHostedHere) return chrome
        const layer = hostedRuntime.chromeLayer
        if (!layer) return chrome
        return createPortal(<div className={dockStyles.hostedChromeItem}>{chrome}</div>, layer)
    }

    const overlay = renderOverlay?.({
        isLoaded: showVideoElement,
        videoError,
        retryCount: videoRetryCount,
        originEl: rootRef.current,
    })
    const playingInDockOverlay = isDockOwned ? (
        <div
            className={styles.playingInDockOverlay}
            data-dock-placeholder=""
            data-post-embed-navigation-exempt="true"
            onClick={event => event.stopPropagation()}
            onMouseDown={event => event.stopPropagation()}
            onPointerDown={event => event.stopPropagation()}
            onTouchStart={event => event.stopPropagation()}
        >
            <span className={styles.playingInDockLabel}>Playing in dock</span>
            <button
                type="button"
                className={styles.playHereButton}
                onClick={event => {
                    event.preventDefault()
                    event.stopPropagation()
                    requestInlinePlayback()
                }}
                aria-label="Play video here"
            >
                <Play className={styles.playHereIcon} aria-hidden="true" />
                Play here
            </button>
        </div>
    ) : null
    const armVideoPreload = useCallback(() => setIsVideoPreloadArmed(true), [])
    const intentPreloadHandlers =
        preloadVideoOnHover && !showVideoElement
            ? {
                  onMouseEnter: armVideoPreload,
                  onMouseMove: armVideoPreload,
                  onPointerEnter: armVideoPreload,
                  onFocusCapture: armVideoPreload,
                  onPointerDown: armVideoPreload,
              }
            : undefined
    const revealChromeForPointerIntent = useCallback(() => {
        if (!usesHoverChrome) return
        clearChromeAutoHideTimeout()
        setIsChromeSurfaceActive(true)
        if (!keepChromeVisibleOnHover || isChromeFullscreenActive()) scheduleChromeAutoHide()
    }, [
        clearChromeAutoHideTimeout,
        isChromeFullscreenActive,
        keepChromeVisibleOnHover,
        scheduleChromeAutoHide,
        usesHoverChrome,
    ])
    const handleChromeSurfaceMouseEnter = useCallback(() => {
        revealChromeForPointerIntent()
    }, [revealChromeForPointerIntent])
    const handleChromeSurfaceMouseMove = useCallback(() => {
        revealChromeForPointerIntent()
    }, [revealChromeForPointerIntent])
    const handleChromeSurfaceTap = useCallback(() => {
        syncChromeFromElement(mediaElementRef.current, { forceTime: true })

        if (usesHoverChrome) {
            revealChromeForPointerIntent()
            return
        }

        // Touch: tapping the video toggles the chrome (chrome taps stop
        // propagation before reaching this surface).
        clearChromeAutoHideTimeout()
        if (isChromeSurfaceActive) {
            setIsChromeSurfaceActive(false)
        } else {
            setIsChromeSurfaceActive(true)
            scheduleChromeAutoHide(2600)
        }
    }, [
        clearChromeAutoHideTimeout,
        isChromeSurfaceActive,
        mediaElementRef,
        revealChromeForPointerIntent,
        scheduleChromeAutoHide,
        syncChromeFromElement,
        usesHoverChrome,
    ])
    const handleChromeSurfaceClick = useCallback(
        event => {
            event.preventDefault()
            event.stopPropagation()
            handleChromeSurfaceTap()
        },
        [handleChromeSurfaceTap],
    )
    // Hosted fullscreen puts the dock's surface on screen, not this frame, so
    // the frame's own handlers never see the pointer: listen on the surface
    // itself, leaving the portalled controls to their own handlers.
    const hostedChromeLayer = hostedRuntime.chromeLayer
    useEffect(() => {
        if (!isFullscreen || !isHostedHere) return undefined
        const fullscreenTarget = getFullscreenTarget()
        if (!fullscreenTarget?.addEventListener) return undefined

        const isControlEvent = event =>
            typeof Node !== 'undefined' &&
            event.target instanceof Node &&
            hostedChromeLayer?.contains(event.target) === true
        const handleMouseMove = event => {
            if (!isControlEvent(event)) revealChromeForPointerIntent()
        }
        const handleClick = event => {
            if (!isControlEvent(event)) handleChromeSurfaceTap()
        }

        fullscreenTarget.addEventListener('mousemove', handleMouseMove)
        fullscreenTarget.addEventListener('click', handleClick)
        return () => {
            fullscreenTarget.removeEventListener('mousemove', handleMouseMove)
            fullscreenTarget.removeEventListener('click', handleClick)
        }
    }, [
        getFullscreenTarget,
        handleChromeSurfaceTap,
        hostedChromeLayer,
        isFullscreen,
        isHostedHere,
        revealChromeForPointerIntent,
    ])
    const handleChromeSurfaceMouseLeave = useCallback(
        event => {
            if (!usesHoverChrome) return
            clearChromeAutoHideTimeout()
            const activeElement = typeof document !== 'undefined' ? document.activeElement : null
            if (
                typeof Node !== 'undefined' &&
                activeElement instanceof Node &&
                event.currentTarget.contains(activeElement) &&
                matchesFocusVisible(activeElement)
            ) {
                return
            }
            if (isChromeFullscreenActive()) {
                scheduleChromeAutoHide(650)
            } else {
                setIsChromeSurfaceActive(false)
            }
        },
        [
            clearChromeAutoHideTimeout,
            isChromeFullscreenActive,
            scheduleChromeAutoHide,
            usesHoverChrome,
        ],
    )
    const handleChromeSurfaceFocus = useCallback(
        event => {
            if (!usesHoverChrome) return
            clearChromeAutoHideTimeout()
            setIsChromeSurfaceActive(true)
            // Window refocus re-fires focus on a pointer-focused control;
            // only keyboard-visible focus should hold the chrome open.
            if (!matchesFocusVisible(event.target)) scheduleChromeAutoHide()
        },
        [clearChromeAutoHideTimeout, scheduleChromeAutoHide, usesHoverChrome],
    )
    const handleChromeSurfaceBlur = useCallback(
        event => {
            if (!usesHoverChrome) return
            if (
                typeof Node !== 'undefined' &&
                event.relatedTarget instanceof Node &&
                event.currentTarget.contains(event.relatedTarget)
            ) {
                return
            }
            clearChromeAutoHideTimeout()
            if (isChromeFullscreenActive()) {
                scheduleChromeAutoHide(500)
            } else {
                setIsChromeSurfaceActive(false)
            }
        },
        [
            clearChromeAutoHideTimeout,
            isChromeFullscreenActive,
            scheduleChromeAutoHide,
            usesHoverChrome,
        ],
    )
    const handleChromeIntentStart = useCallback(
        event => {
            if (!usesHoverChrome) return
            if (event?.type === 'focus' && !matchesFocusVisible(event.target)) {
                // Window refocus re-fires focus on a pointer-focused control —
                // show briefly, but don't hold the chrome open.
                clearChromeAutoHideTimeout()
                setIsChromeSurfaceActive(true)
                scheduleChromeAutoHide()
                return
            }
            chromeIntentActiveRef.current = true
            clearChromeAutoHideTimeout()
            setIsChromeSurfaceActive(true)
        },
        [clearChromeAutoHideTimeout, scheduleChromeAutoHide, usesHoverChrome],
    )
    const handleChromeIntentEnd = useCallback(
        event => {
            if (!usesHoverChrome) return
            chromeIntentActiveRef.current = false
            const relatedTarget = event?.relatedTarget

            if (
                typeof Node !== 'undefined' &&
                relatedTarget instanceof Node &&
                (event.currentTarget.contains(relatedTarget) ||
                    (keepChromeVisibleOnHover &&
                        playbackContainerRef.current?.contains(relatedTarget)))
            ) {
                return
            }

            scheduleChromeAutoHide(650)
        },
        [keepChromeVisibleOnHover, scheduleChromeAutoHide, usesHoverChrome],
    )

    return (
        <div
            ref={rootRef}
            className={cn(styles.root, className)}
            style={rootStyle}
            data-media-track-origin={mediaKey || undefined}
            onClick={event => event.stopPropagation()}
        >
            {posterUrl && !showVideoElement ? (
                <link rel="preload" as="image" href={posterUrl} />
            ) : null}
            {shouldRenderVideoPreload ? <link rel="preload" as="video" href={currentSrc} /> : null}

            {!showVideoElement ? (
                <div
                    ref={previewContainerRef}
                    className={cn(styles.container, containerClassName)}
                    data-video-player-container="true"
                    // The frame the dock-owned placeholder stands in for.
                    data-dock-placeholder-host=""
                    style={{ aspectRatio: containerAspectRatio || undefined, ...containerStyle }}
                    onClick={event => event.stopPropagation()}
                    {...intentPreloadHandlers}
                >
                    {overlay}
                    {renderChromeInDock(topRightControls)}
                    {posterUrl ? (
                        <img
                            className={cn(styles.poster, posterClassName)}
                            src={posterUrl}
                            alt=""
                            width={width || undefined}
                            height={height || undefined}
                            onLoad={handlePosterLoad}
                            onError={handlePosterError}
                        />
                    ) : hasFirstFramePreview ? (
                        <video
                            src={currentSrc || undefined}
                            muted
                            playsInline
                            preload="metadata"
                            tabIndex={-1}
                            aria-hidden="true"
                            data-testid="video-first-frame-preview"
                            className={cn(styles.poster, posterClassName)}
                            onLoadedMetadata={handleFirstFrameLoadedMetadata}
                            onError={handleVideoError}
                        />
                    ) : (
                        <span className={styles.placeholder} aria-hidden="true" />
                    )}
                    {playingInDockOverlay}
                    {!isDockOwned && (
                        <div className={styles.playButtonContainer}>
                            <MediaPlayButton
                                data-answer-interactive="true"
                                onKeyDown={handleStartPlaybackKeyDown}
                                onClick={handleStartPlayback}
                                aria-label="Play video"
                            />
                        </div>
                    )}
                </div>
            ) : (
                <div
                    ref={setPlaybackContainerNode}
                    className={cn(styles.container, containerClassName)}
                    data-video-player-container="true"
                    // The frame the dock-owned placeholder stands in for.
                    data-dock-placeholder-host=""
                    style={{ aspectRatio: containerAspectRatio || undefined, ...containerStyle }}
                    data-post-embed-navigation-exempt="true"
                    onMouseEnter={handleChromeSurfaceMouseEnter}
                    onMouseMove={handleChromeSurfaceMouseMove}
                    onMouseLeave={handleChromeSurfaceMouseLeave}
                    onFocusCapture={handleChromeSurfaceFocus}
                    onBlurCapture={handleChromeSurfaceBlur}
                >
                    {overlay}
                    {videoError ? (
                        <div className={styles.videoError}>
                            <AlertTriangle className={styles.statusIcon} aria-hidden="true" />
                            Video temporarily unavailable
                        </div>
                    ) : null}
                    {videoRetryCount > 0 && !videoError ? (
                        <div className={styles.retryNotice}>
                            <Loader2 className={styles.retrySpinner} aria-hidden="true" />
                            Retrying...
                        </div>
                    ) : null}
                    {hostedVideo ? null : (
                        <video
                            autoPlay={shouldAutoPlay}
                            src={currentSrc || undefined}
                            width={width || undefined}
                            height={height || undefined}
                            onLoadedMetadata={handleLoadedMetadata}
                            onPlay={handlePlay}
                            onPause={handlePause}
                            onEnded={handleEnded}
                            onSeeking={handleSeeking}
                            onTimeUpdate={handleVideoTimeUpdate}
                            onSeeked={handleVideoSeeked}
                            onVolumeChange={handleVolumeChange}
                            onRateChange={handlePlaybackRateChange}
                            onError={handleVideoError}
                            controls={false}
                            playsInline
                            preload={preload}
                            ref={mediaElementRef}
                            className={cn(styles.video, videoClassName)}
                        />
                    )}
                    <div
                        className={styles.interactionSurface}
                        data-testid="native-video-interaction-surface"
                        onClick={handleChromeSurfaceClick}
                        aria-hidden="true"
                    />
                    {renderChromeInDock(topRightControls)}
                    {!isDockOwned && !videoError
                        ? renderChromeInDock(
                              <VideoChrome
                                  variant="overlay"
                                  isPlaying={chromeState.isPlaying}
                                  currentTime={chromeState.currentTime}
                                  duration={chromeState.duration}
                                  volume={chromeState.volume}
                                  playbackRate={chromeState.playbackRate}
                                  isFullscreen={isFullscreen}
                                  playbackRates={VIDEO_PLAYBACK_RATES}
                                  captionsAvailable={chromeState.captionsAvailable}
                                  captionsEnabled={chromeState.captionsEnabled}
                                  visible={showChrome}
                                  onTogglePlayback={handleChromeTogglePlayback}
                                  onSeek={handleChromeSeek}
                                  onSeekStart={handleChromeSeekStart}
                                  onSeekEnd={handleChromeSeekEnd}
                                  onVolumeChange={handleChromeVolume}
                                  onPlaybackRateChange={handleChromePlaybackRate}
                                  onToggleCaptions={handleChromeToggleCaptions}
                                  onFullscreen={handleChromeFullscreen}
                                  inPlaylist={inPlaylist}
                                  playlistCount={shownPlaylistCount}
                                  onTogglePlaylist={
                                      mediaKey ? handleChromeTogglePlaylist : undefined
                                  }
                                  onChromeIntentStart={handleChromeIntentStart}
                                  onChromeIntentEnd={handleChromeIntentEnd}
                                  dataTestId="native-video-chrome"
                              />,
                          )
                        : null}
                    {playingInDockOverlay}
                </div>
            )}
        </div>
    )
}

export default React.memo(VideoPlayerCore)
